<script setup lang="ts">import { toRef as _toRef, computed as _computed, ref as _ref } from 'vue';

import {PerfectScrollbar} from 'vue3-perfect-scrollbar'
import '../../../styles/perfect-scrollbar-style.scss'

import {useScroll, useRafFn, useMutationObserver} from '@vueuse/core'
import {nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect} from 'vue'
import PerfectScrollbarNamespace from 'perfect-scrollbar'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {converter, formatRgb} from 'culori'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import {ThemeVariableType} from '../../theme/enum/ThemeVariableType'
import {useAppContentAnimation} from '@toolify/client/src/modules/layout/composables/useAppContentAnimation'
import {
  attachScrollLock,
  detachScrollLock,
  IScrollLockListeners,
  scrollLockClass,
} from '@toolify/client/src/modules/ui/functions/scrollLock'
import {
  cancelLayoutRead,
  cancelLayoutWrite,
  scheduleLayoutRead,
  scheduleLayoutWrite,
} from '@toolify/client/src/modules/ui/functions/scheduleLayoutRead'

interface Props {
  scrollDownOnMounted?: boolean
  gradientColor: string
  options?: PerfectScrollbarNamespace.Options
  gradientDirections?: ('bottom'|'left'|'top'|'right')[]
  gradientOffsets?: {direction: 'bottom'|'left'|'top'|'right', offset: number|string}[]
  scroll?: {
    x: number
    y: number
  }
  visibleScrollbarOnlyOnHover?: boolean
  isDisabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  scrollDownOnMounted: false,
  options() {
    return {}
  },
  gradientDirections() {
    return ['bottom', 'left', 'top', 'right']
  },
  gradientOffsets() {
    return []
  },
  scroll() {
    return {
      x: 0,
      y: 0,
    }
  },
  visibleScrollbarOnlyOnHover: false,
  isDisabled: false,
})

interface Emits {
  (e: 'scroll', payload: {
    scrollHeight: number,
    scrollWidth: number,
    x: number,
    y: number,
    width: number,
    height: number,
  }): void
  (e: 'update:scroll', scroll: {
    x: number
    y: number
  }): void
}
const emit = defineEmits<Emits>()

const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const __$temp_2 = (useResponsive()),
  canHover = _toRef(__$temp_2, 'canHover'),
  isSmallerThanLarge = _toRef(__$temp_2, 'isSmallerThanLarge');

// Use native scrolling on smaller screens (mobile/tablet) for natural momentum scrolling
const useNativeScrolling = _computed(() => isSmallerThanLarge.value || !canHover.value )

// If device cannot hover, always show scrollbar (don't hide on non-hover)
const effectiveVisibleOnlyOnHover = _computed(() => {
  return props.visibleScrollbarOnlyOnHover && canHover.value
})

let scrollAreaRef = _ref<{$el: HTMLElement}>(null)
interface IScrollAreaInstanceRef {
  $el?: HTMLElement
  update?: () => void
  ps?: {
    update?: () => void
    containerWidth?: number
    containerHeight?: number
  }
}
let scrollAreaInstanceRef = _ref<IScrollAreaInstanceRef | HTMLElement>(null)
let lastLockedPosition = _ref({
  x: 0,
  y: 0,
})
let resizeObserver = null
const measuredScrollSize = ref({
  height: 0,
  width: 0,
})


const handleScrollEvent = (event: WheelEvent | TouchEvent) => {
  if (props.isDisabled) {
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}

const handleScrollPosition = () => {
  if (props.isDisabled && scrollAreaRef.value?.$el) {
    scrollAreaRef.value.$el.scrollTop = lastLockedPosition.value.y
    scrollAreaRef.value.$el.scrollLeft = lastLockedPosition.value.x
  }
}

const handleKeydown = (e: KeyboardEvent) => {
  if (!props.isDisabled) return

  const target = e.target as HTMLElement
  const isEditableElement =
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable

  if (isEditableElement) return

  const blockedKeys = [
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    ' ',
  ]

  if (blockedKeys.includes(e.key)) {
    e.preventDefault()
  }
}


const scrollLockListeners: IScrollLockListeners = {
  blockScrollGesture: handleScrollEvent,
  restoreScrollPosition: handleScrollPosition,
  blockScrollKeydown: handleKeydown,
}

const releaseScrollLock = () => {
  if (!scrollAreaRef.value?.$el) {
    return
  }
  detachScrollLock(scrollAreaRef.value.$el, scrollLockListeners)
}

watch(() => props.isDisabled, (newValue, oldValue) => {
  if (!scrollAreaRef.value?.$el) {
    return
  }

  if(oldValue === newValue) return
  
  if (newValue) {
    lastLockedPosition.value = { 
      x: scrollAreaRef.value.$el.scrollLeft, 
      y: scrollAreaRef.value.$el.scrollTop, 
    }
    
    attachScrollLock(scrollAreaRef.value.$el, scrollLockListeners)
  } else {
    releaseScrollLock()
  }
}, {immediate: true})

function detachScrollAreaElement(element: HTMLElement) {
  resizeObserver?.unobserve(element)
  detachScrollLock(element, scrollLockListeners)
  element.removeEventListener('wheel', cancelSmoothScrollToY)
  element.removeEventListener('touchstart', cancelSmoothScrollToY)
}

const scrollableAreaRef = _computed(() => {
  if(!scrollAreaRef.value || !hasMounted.value) {
    return null
  }
  return scrollAreaRef.value.$el
})
let __$temp_3 = (useScroll((scrollableAreaRef))),
  x = _toRef(__$temp_3, 'x'),
  y = _toRef(__$temp_3, 'y');
let height = _ref(0)
let width = _ref(0)
// const {width, height, update} = $(useElementBounding($$(scrollableAreaRef)))
let hasMounted = _ref(false)
let perfectScrollbarComponentRef = _ref(null)

function syncScrollAreaDimensionsFromElement() {
  if (!scrollAreaRef.value?.$el) {
    return
  }
  const newWidth = scrollAreaRef.value.$el.clientWidth
  const newHeight = scrollAreaRef.value.$el.clientHeight
  if (newWidth !== width.value || newHeight !== height.value) {
    width.value = newWidth
    height.value = newHeight
  }
}

watch(() => props.scroll, (scroll, previousScroll) => {
  if(!scrollAreaRef.value?.$el || props.isDisabled) {
    return
  }
  if(scroll.y !== previousScroll?.y && y.value !== scroll.y) {
    scrollAreaRef.value.$el.scrollTop = scroll.y
  }
  if(scroll.x !== previousScroll?.x && x.value !== scroll.x) {
    scrollAreaRef.value.$el.scrollLeft = scroll.x
  }
}, {
  immediate: true,
})

watch([() => x.value, () => y.value, () => width.value, () => height.value], () => {
  if(!scrollAreaRef.value || !hasMounted.value) {
    return
  }
  onScrollUpdate()
})


let lastEmitted = {
  scrollHeight: 0,
  scrollWidth: 0,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
}

const onScrollUpdate = () => {
  const scrollHeight = scrollAreaRef.value.$el.scrollHeight
  const scrollWidth = scrollAreaRef.value.$el.scrollWidth
  if(measuredScrollSize.value.height !== scrollHeight || measuredScrollSize.value.width !== scrollWidth) {
    measuredScrollSize.value = {
      height: scrollHeight,
      width: scrollWidth,
    }
  }
  if (
    lastEmitted.scrollHeight === scrollHeight &&
    lastEmitted.scrollWidth === scrollWidth &&
    lastEmitted.x === x.value &&
    lastEmitted.y === y.value &&
    lastEmitted.width === width.value &&
    lastEmitted.height === height.value
  ) {
    return
  }
  
  lastEmitted = {
    scrollHeight,
    scrollWidth,
    x: x.value,
    y: y.value,
    width: width.value,
    height: height.value,
  }

  emit('scroll', {
    scrollHeight,
    scrollWidth,
    x: x.value,
    y: y.value,
    width: width.value,
    height: height.value,
  })
}

const updateScrollMeasurements = () => {
  if(!scrollAreaRef.value?.$el) {
    return
  }
  const newWidth = scrollAreaRef.value.$el.clientWidth
  const newHeight = scrollAreaRef.value.$el.clientHeight
  if(newWidth !== width.value || newHeight !== height.value) {
    width.value = newWidth
    height.value = newHeight
  }
  onScrollUpdate()
}

const refreshPerfectScrollbar = () => {
  if(!scrollAreaInstanceRef.value || useNativeScrolling.value) {
    return
  }
  if('update' in scrollAreaInstanceRef.value && typeof scrollAreaInstanceRef.value.update === 'function') {
    scrollAreaInstanceRef.value.update()
  }
  if('ps' in scrollAreaInstanceRef.value && scrollAreaInstanceRef.value.ps?.update) {
    scrollAreaInstanceRef.value.ps.update()
    refreshAgainIfMeasuredUnderTransform(scrollAreaInstanceRef.value.ps)
  }
}

const ANIMATION_END_EVENTS = ['transitionend', 'animationend']
let layoutSize = {
  width: 0,
  height: 0,
}
let stopWaitingForAncestorAnimation: (() => void) | null = null

const refreshAfterAncestorAnimation = (event: Event) => {
  if(!(event.target instanceof Node) || !event.target.contains(scrollAreaRef.value?.$el ?? null)) {
    return
  }
  stopWaitingForAncestorAnimation?.()
  lastPerfectScrollbarDimensions = ''
  scheduleRefreshScrollbar()
}

const waitForAncestorAnimation = () => {
  if(stopWaitingForAncestorAnimation) {
    return
  }
  for(const eventName of ANIMATION_END_EVENTS) {
    window.addEventListener(eventName, refreshAfterAncestorAnimation, true)
  }
  stopWaitingForAncestorAnimation = () => {
    for(const eventName of ANIMATION_END_EVENTS) {
      window.removeEventListener(eventName, refreshAfterAncestorAnimation, true)
    }
    stopWaitingForAncestorAnimation = null
  }
}

const refreshAgainIfMeasuredUnderTransform = (ps: IScrollAreaInstanceRef['ps']) => {
  const sizeDifference = Math.max(
    Math.abs(ps.containerWidth - layoutSize.width),
    Math.abs(ps.containerHeight - layoutSize.height),
  )
  scrollAreaRef.value?.$el?.classList.toggle('is-measured-under-transform', sizeDifference > 1)
  if(sizeDifference > 0) {
    waitForAncestorAnimation()
  }
}

const isScrollAreaHidden = () => {
  const element = scrollAreaRef.value?.$el
  if(!element) {
    return true
  }
  return !element.clientWidth && !element.clientHeight
}

let lastPerfectScrollbarDimensions = ''

const refreshScrollbarMeasurements = () => {
  if(isScrollAreaHidden()) {
    return false
  }
  updateScrollMeasurements()
  layoutSize = {
    width: scrollAreaRef.value.$el.offsetWidth,
    height: scrollAreaRef.value.$el.offsetHeight,
  }
  const dimensions = `${width.value}x${height.value}:${measuredScrollSize.value.width}x${measuredScrollSize.value.height}`
  if(dimensions === lastPerfectScrollbarDimensions) {
    return false
  }
  lastPerfectScrollbarDimensions = dimensions
  return true
}

const refreshScrollbar = () => {
  if(refreshScrollbarMeasurements()) {
    refreshPerfectScrollbar()
  }
}

const runScheduledRefreshScrollbar = () => {
  if(refreshScrollbarMeasurements()) {
    scheduleLayoutWrite(refreshPerfectScrollbar)
  }
}

const clearRefreshSchedule = () => {
  cancelLayoutRead(runScheduledRefreshScrollbar)
  cancelLayoutWrite(refreshPerfectScrollbar)
}

// Every refresh forces a layout, so all mutations of a frame share one refresh before paint.
const scheduleRefreshScrollbar = () => {
  scheduleLayoutRead(runScheduledRefreshScrollbar)
}

const appContentAnimation = useAppContentAnimation()
const stopAnimationBusListener = appContentAnimation?.animationBus?.on(() => {
  scheduleRefreshScrollbar()
})

useMutationObserver((scrollableAreaRef), () => {
  scheduleRefreshScrollbar()
}, {
  childList: true,
  subtree: true,
})

watchEffect(() => {
  if(!scrollAreaRef.value || !hasMounted.value) {
    return
  }
  if(x.value !== props.scroll.x || y.value !== props.scroll.y) {
    emit('update:scroll', {
      x: x.value,
      y: y.value,
    })
  }
})

onMounted(() => {
  nextTick(() => {
    hasMounted.value = true
    scheduleRefreshScrollbar()
    if(props.scrollDownOnMounted) {
      scrollToBottom()
    }
    scrollAreaRef.value?.$el?.addEventListener('wheel', cancelSmoothScrollToY, {passive: true})
    scrollAreaRef.value?.$el?.addEventListener('touchstart', cancelSmoothScrollToY, {passive: true})
  })
})

const rgbConverter = converter('rgb')
const rgbColors = _computed(() => {
  const raw = props.gradientColor.trim()
  if (!raw) {
    return [] as string[]
  }
  const color = rgbConverter(raw)
  if (!color) {
    return [] as string[]
  }
  const secondColor = {
    ...color,
    alpha: 0,
  }
  return [
    formatRgb(color),
    formatRgb(secondColor),
  ]
})
const marginDistances = _computed(() => {
  if(!scrollAreaRef.value) {
    return null
  }
  const {height: scrollHeight, width: scrollWidth} = measuredScrollSize.value
  return {
    top: y.value,
    left: x.value,
    bottom: scrollHeight - height.value - y.value,
    right: scrollWidth - width.value - x.value,
  }
})
const minDistance = 1
const maxDistance = 30
const degreesByDirection = new Map([
  ['bottom', 90 * 0],
  ['left', 90 * 1],
  ['top', 90 * 2],
  ['right', 90 * 3],
])
function formatGradientOffset(offset: number | string): string {
  if(typeof offset === 'number') {
    return `${offset}px`
  }
  return offset
}

const gradientOffsetByDirection = _computed(() => {
  return new Map(props.gradientOffsets.map(gradientOffset => {
    return [gradientOffset.direction, formatGradientOffset(gradientOffset.offset)] as const
  }))
})

const gradients = _computed(() => {
  if(!marginDistances.value) {
    return []
  }
  return props.gradientDirections.map((direction) => {
    const distance = marginDistances.value[direction]

    const opacity = Math.min(1, Math.max(0, (distance - minDistance) / maxDistance))
    const style: Record<string, string> = {
      opacity: `${opacity * 90}%`,
      background: rgbColors.value?.length ? `linear-gradient(${degreesByDirection.get(direction)}deg, ${rgbColors.value[0]} 0%, ${rgbColors.value[1]} 100%)` : 'transparent',
    }
    const offset = gradientOffsetByDirection.value.get(direction)
    if(offset) {
      style[direction] = offset
    }
    return {
      direction,
      style,
    }
  })
})

const getElementFromRef = (ref: IScrollAreaInstanceRef | HTMLElement): HTMLElement | null => {
  if(ref instanceof HTMLElement) {
    return ref
  }
  return ref.$el || null
}
const setScrollAreaRef = (ref: unknown) => {
  if(!ref) {
    if(scrollAreaRef.value?.$el) {
      detachScrollAreaElement(scrollAreaRef.value.$el)
    }
    scrollAreaRef.value = null
    scrollAreaInstanceRef.value = null
    perfectScrollbarComponentRef.value = null
    return
  }

  const isElement = ref instanceof HTMLElement
  const isObject = typeof ref === 'object'
  if(!isElement && !isObject) {
    return
  }
  const typedRef = ref as IScrollAreaInstanceRef | HTMLElement
  scrollAreaInstanceRef.value = typedRef
  const element = getElementFromRef(typedRef)
  if(!element) {
    return
  }

  if(scrollAreaRef.value?.$el === element) {
    return
  }

  if(scrollAreaRef.value?.$el) {
    detachScrollAreaElement(scrollAreaRef.value.$el)
  }

  scrollAreaRef.value = {
    $el: element,
  }

  if(!resizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      const newWidth = scrollAreaRef.value.$el.clientWidth
      const newHeight = scrollAreaRef.value.$el.clientHeight
      if (newWidth !== width.value || newHeight !== height.value) {
        width.value = newWidth
        height.value = newHeight
      }
      refreshScrollbar()
    })
  }
  resizeObserver.observe(scrollAreaRef.value.$el)
  lastPerfectScrollbarDimensions = ''
  scheduleRefreshScrollbar()
}

onBeforeUnmount(() => {
  cancelSmoothScrollToY()
  if(scrollAreaRef.value?.$el) {
    detachScrollAreaElement(scrollAreaRef.value.$el)
  }
  resizeObserver?.disconnect()
  resizeObserver = null
  stopAnimationBusListener?.()
  stopWaitingForAncestorAnimation?.()
  clearRefreshSchedule()
})

const scrollToBottom = () => {
  y.value = scrollAreaRef.value.$el.scrollHeight
}

const scrollByX = (value) => {
  const maxWidth = scrollAreaRef.value.$el.scrollWidth
  if(value < 0) {
    if(x.value + value < 0) {
      x.value = 0
      return
    }
    return x.value = x.value + value
  }
  if(x.value + value > maxWidth) {
    return x.value = maxWidth
  }
  return x.value = x.value + value
}

const scrollByY = (value) => {
  const maxHeight = scrollAreaRef.value.$el.scrollHeight
  if(value < 0) {
    if(y.value + value < 0) {
      y.value = 0
      return
    }
    return y.value = y.value + value
  }
  if(y.value + value > maxHeight) {
    return y.value = maxHeight
  }
  return y.value = y.value + value
}

const scrollToX = async (value) => {
  const maxWidth = scrollAreaRef.value.$el.scrollWidth
  if(value < 0) {
    return x.value = 0
  }
  if(value > maxWidth) {
    return x.value = maxWidth
  }
  x.value = value
}

const scrollToY = (value) => {
  const maxHeight = scrollAreaRef.value.$el.scrollHeight
  if(value < 0) {
    return y.value = 0
  }
  if(value > maxHeight) {
    return y.value = maxHeight
  }
  y.value = value
}

const scrollToTop = () => {
  if(!scrollAreaRef.value?.$el) {
    return
  }
  scrollAreaRef.value.$el.scrollTop = 0
  y.value = 0
}

const getScrollHeight = () => {
  if(!scrollAreaRef.value) {
    return 0
  }
  return scrollAreaRef.value.$el.scrollHeight
}


const getScrollTop = () => {
  if (!scrollAreaRef.value || !scrollAreaRef.value.$el) {
    return 0
  }
  return scrollAreaRef.value.$el.scrollTop
}
const getScrollBottom = () => {
  if (!scrollAreaRef.value || !scrollAreaRef.value.$el) {
    return 0
  }

  const element = scrollAreaRef.value.$el
  const scrollTop = element.scrollTop
  const scrollHeight = element.scrollHeight
  const clientHeight = element.clientHeight

  return scrollHeight - (scrollTop + clientHeight)
}

const scrollToPercentage = (percentage) => {
  y.value = (scrollAreaRef.value.$el.scrollHeight * percentage) / 100
}

const scrollToPercentageX = (percentage) => {
  x.value = (scrollAreaRef.value.$el.scrollWidth * percentage) / 100
}

let smoothScrollToXStart = 0
let smoothScrollToXChange = 0
let smoothScrollToXDuration = 0
let smoothScrollToXStartTime = 0

const {pause: pauseSmoothScrollToX, resume: resumeSmoothScrollToX} = useRafFn(() => {
  const elapsed = performance.now() - smoothScrollToXStartTime
  if(elapsed >= smoothScrollToXDuration) {
    x.value = smoothScrollToXStart + smoothScrollToXChange
    pauseSmoothScrollToX()
    return
  }
  x.value = easeInOutQuad(elapsed, smoothScrollToXStart, smoothScrollToXChange, smoothScrollToXDuration)
}, {immediate: false})

const cancelSmoothScrollToX = () => {
  pauseSmoothScrollToX()
}

const smoothScrollToX = (value, speed = 1) => {
  cancelSmoothScrollToX()
  const maxWidth = scrollAreaRef.value.$el.scrollWidth
  if(value < 0) {
    return x.value = 0
  }
  if(value > maxWidth) {
    return x.value = maxWidth
  }
  smoothScrollToXStart = x.value
  smoothScrollToXChange = value - smoothScrollToXStart
  smoothScrollToXDuration = 500 / speed
  smoothScrollToXStartTime = performance.now()
  resumeSmoothScrollToX()
}

const smoothScrollToPercentX = (percentage, speed = 1) => {
  const value = (scrollAreaRef.value.$el.scrollWidth * percentage) / 100
  smoothScrollToX(value, speed)
}

let smoothScrollToYStart = 0
let smoothScrollToYChange = 0
let smoothScrollToYDuration = 0
let smoothScrollToYStartTime = 0

const {pause: pauseSmoothScrollToY, resume: resumeSmoothScrollToY} = useRafFn(() => {
  const elapsed = performance.now() - smoothScrollToYStartTime
  if(elapsed >= smoothScrollToYDuration) {
    y.value = smoothScrollToYStart + smoothScrollToYChange
    pauseSmoothScrollToY()
    return
  }
  y.value = easeInOutQuad(elapsed, smoothScrollToYStart, smoothScrollToYChange, smoothScrollToYDuration)
}, {immediate: false})

const cancelSmoothScrollToY = () => {
  pauseSmoothScrollToY()
}

const smoothScrollToY = (value, speed = 1) => {
  cancelSmoothScrollToY()
  const maxHeight = scrollAreaRef.value.$el.scrollHeight
  if(value < 0) {
    return y.value = 0
  }
  if(value > maxHeight) {
    return y.value = maxHeight
  }
  smoothScrollToYStart = y.value
  smoothScrollToYChange = value - smoothScrollToYStart
  smoothScrollToYDuration = 500 / speed
  smoothScrollToYStartTime = performance.now()
  resumeSmoothScrollToY()
}

function easeInOutQuad(time, initialValue, changeInValue, duration) {
  time /= duration / 2
  if (time < 1) return changeInValue / 2 * time * time + initialValue
  time--
  return -changeInValue / 2 * (time * (time - 2) - 1) + initialValue
}


defineExpose({
  refresh: refreshScrollbar,
  scrollToBottom,
  scrollToTop,
  scrollToPercentage,
  scrollToPercentageX,
  scrollByX,
  scrollToX,
  scrollByY,
  scrollToY,
  getScrollHeight,
  getScrollTop,
  getScrollBottom,
  smoothScrollToX,
  smoothScrollToPercentX,
  smoothScrollToY,
  cancelSmoothScrollToY,
  releaseScrollLock,
})

const scrollAreaClasses = _computed(() => {
  return {
    [scrollLockClass]: props.isDisabled,
  }
})
const canUseGradients = _computed(() => {
  return getThemeVariable.value(ThemeVariableType.UseScrollbarGradients)
})

</script>

<template>
  <div
    class="scrollbar"
    :class="{'is-native-scroll': useNativeScrolling}"
    ref="scrollbarRef"
  >
    <div 
      v-if="useNativeScrolling" 
      class="scroll-area native-scroll"
      :class="scrollAreaClasses"
      :ref="setScrollAreaRef"
    >
      <slot/>
    </div>
    <PerfectScrollbar
      v-else
      class="scroll-area"
      :class="[scrollAreaClasses, { 'visible-scrollbar-only-on-hover': effectiveVisibleOnlyOnHover }]"
      :options="options"
      :ref="setScrollAreaRef"
    >
      <slot/>
    </PerfectScrollbar>
    <template v-if="canUseGradients">
      <div
        class="gradient"
        :class="gradient.direction"
        v-for="gradient in gradients"
        :style="gradient.style"
        :key="gradient.direction"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;
.scrollbar {
  width: 100%;
  height: 100%;
  min-height: 0;
  position: relative;
}

.scroll-area {
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;

  &.native-scroll {
    overflow: auto;
    -webkit-overflow-scrolling: touch; /* Enable momentum scrolling on iOS */
    
    &::-webkit-scrollbar {
      width: var(--scrollbar-rail-y-width);
      height: var(--scrollbar-rail-x-height);
      background: transparent;
      z-index: var(--scrollbar-rail-z-index);
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      z-index: var(--scrollbar-rail-z-index);
      background-color: map-get(variables.$colors, scrollbar-thumb);
      border-radius: map-get(variables.$radiuses, radius-lg);
      
      &:hover {
        background-color: map-get(variables.$colors, scrollbar-thumb-hover);
      }

      &:active {
        background-color: map-get(variables.$colors, scrollbar-thumb-active);
      }
    }

    @include variables.scrollbarFallback(map-get(variables.$colors, scrollbar-thumb));
  }
}

.visible-scrollbar-only-on-hover > :deep(.ps__rail-x),
.visible-scrollbar-only-on-hover > :deep(.ps__rail-y) {
  opacity: 0;
  visibility: hidden;
}

.visible-scrollbar-only-on-hover.ps--scrolling-x > :deep(.ps__rail-x),
.visible-scrollbar-only-on-hover.ps--scrolling-y > :deep(.ps__rail-y),
.visible-scrollbar-only-on-hover > :deep(.ps__rail-x.ps--clicking),
.visible-scrollbar-only-on-hover > :deep(.ps__rail-y.ps--clicking) {
  opacity: 1;
  visibility: visible;
}

@media (hover: hover) and (pointer: fine) {
  .scroll-area:hover > :deep(.ps__rail-x),
  .scroll-area:hover > :deep(.ps__rail-y) {
    opacity: 1;
    visibility: visible;
  }
}
.scroll-area.disabled-scroll {
  overflow: hidden !important;
  :deep(.ps__rail-x),
  :deep(.ps__rail-y) {
    display: none !important;
  }
}
.scroll-area.is-measured-under-transform > :deep(.ps__rail-x),
.scroll-area.is-measured-under-transform > :deep(.ps__rail-y) {
  visibility: hidden !important;
  transition: none !important;
}

.gradient {
  position: absolute;
  z-index: var(--scrollbar-gradient-z-index);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &.bottom {
    bottom: 0;
    background: linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  }

  &.left {
    left: 0;
    background: linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  }

  &.top {
    top: 0;
    background: linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  }

  &.right {
    right: 0;
    background: linear-gradient(270deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%);
  }

  &.top, &.bottom {
    width: 100%;
    height: 30px;
    left: 0;
  }

  &.left, &.right {
    height: 100%;
    width: 30px;
    top: 0;
  }
}

.is-native-scroll .gradient {
  &.top,
  &.bottom {
    width: calc(100% - var(--scrollbar-rail-y-width));
  }

  &.left,
  &.right {
    height: calc(100% - var(--scrollbar-rail-x-height));
  }

  &.right {
    right: var(--scrollbar-rail-y-width);
  }
}
</style>
